import React from 'react'
import { Autocomplete, Box, Chip, TextField } from '@mui/material'
import ReactCountryFlag from 'react-country-flag'
import { ALL_COUNTRY_NAMES, resolveCountry } from '../../utilities/countryLookup'

interface ActivityCountriesFieldProps {
    value: string[]
    onChange: (val: string[]) => void
    disabled?: boolean
    keyMarketNames?: string[]
    error?: boolean
    helperText?: React.ReactNode
}

const ActivityCountriesField: React.FC<ActivityCountriesFieldProps> = ({
    value,
    onChange,
    disabled,
    keyMarketNames = [],
    error,
    helperText,
}) => {
    const options = Array.from(new Set([...keyMarketNames, ...ALL_COUNTRY_NAMES]))

    if (disabled) {
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {value.map((country) => {
                    const { code, label } = resolveCountry(country)
                    return (
                        <Chip
                            key={country}
                            size="small"
                            icon={
                                code ? (
                                    <ReactCountryFlag
                                        countryCode={code}
                                        svg
                                        cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                        cdnSuffix="svg"
                                        style={{ width: '1em', height: '1em' }}
                                        title={label}
                                        className="countryFlag"
                                    />
                                ) : undefined
                            }
                            label={label}
                            sx={{ borderRadius: '30px', backgroundColor: 'figma.primary3' }}
                        />
                    )
                })}
            </Box>
        )
    }

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={value}
            onChange={(_e, newValue) => onChange(newValue)}
            groupBy={(opt) => (keyMarketNames.includes(opt) ? 'Key markets' : 'All countries')}
            isOptionEqualToValue={(o, v) => o === v}
            renderOption={(props, option) => {
                const { code, label } = resolveCountry(option)
                return (
                    <li {...props} key={option} style={{ gap: 8 }}>
                        {code && (
                            <ReactCountryFlag
                                countryCode={code}
                                svg
                                cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                cdnSuffix="svg"
                                style={{ width: '1.4em', height: '1.4em' }}
                                title={label}
                                className="countryFlag"
                            />
                        )}
                        <span>{label}</span>
                    </li>
                )
            }}
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => {
                    const { code, label } = resolveCountry(option)
                    return (
                        <Chip
                            {...getTagProps({ index })}
                            key={option}
                            size="small"
                            icon={
                                code ? (
                                    <ReactCountryFlag
                                        countryCode={code}
                                        svg
                                        cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                        cdnSuffix="svg"
                                        style={{ width: '1em', height: '1em' }}
                                        title={label}
                                        className="countryFlag"
                                    />
                                ) : undefined
                            }
                            label={label}
                            sx={{ borderRadius: '30px', backgroundColor: 'figma.primary3' }}
                        />
                    )
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={value.length ? '' : 'Add countries...'}
                    error={error}
                    helperText={helperText || ' '}
                    sx={{
                        '.MuiInputBase-root': { borderRadius: '12px' },
                        '& input::placeholder': {
                            color: 'figma.primary',
                            opacity: 1,
                            fontWeight: 600,
                            fontSize: '1rem',
                        },
                    }}
                />
            )}
        />
    )
}

export default ActivityCountriesField
